import { updateMemberSubscription } from 'actions/LuxPlusActions'
import TextArea from 'components/Common/Form/Input/TextArea'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import type { ChangeEventHandler, FormEventHandler } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipStatusBanner from './LuxPlusManageAccountModalMembershipStatusBanner'
import LuxPlusManageAccountModalSavingsBanner from './LuxPlusManageAccountModalSavingsBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

const REASONS = [
  'The annual fee is too expensive',
  'I don’t travel frequently enough',
  'The benefits are not meeting my expectations',
  'I don’t want to be automatically charged',
  'Other (please specify)',
] as const

type ReasonValue = typeof REASONS[number]

interface CancelRenewalFormDetails {
  reason?: string
  comment?: string
}

interface Props {
  item: App.MemberSubscriptionItem
  onRenewCancelSuccess: () => void
  onBack: () => void
}

function LuxPlusManageAccountModalRenewalCancellation({
  item,
  onRenewCancelSuccess,
  onBack,
}: Props) {
  const intl = useIntl()

  const reasonLabels = useMemo<Record<ReasonValue, string>>(() => ({
    'The annual fee is too expensive': intl.formatMessage({
      id: 'SUEvSckO0NAqT2Chk8O8',
      defaultMessage: 'The annual fee is too expensive',
      description: 'Cancellation reason option: cost is too high in the cancellation flow',
    }),
    'I don’t travel frequently enough': intl.formatMessage({
      id: '2U4zFTgvZmndC1IiHxIh',
      defaultMessage: 'I don’t travel frequently enough',
      description: 'Cancellation reason option: infrequent travel in a modal dialog',
    }),
    'The benefits are not meeting my expectations': intl.formatMessage({
      id: '3LvBL4X2dgEEtbVeBqGb',
      defaultMessage: 'The benefits are not meeting my expectations',
      description: 'Cancellation reason option: unsatisfied with benefits',
    }),
    'I don’t want to be automatically charged': intl.formatMessage({
      id: 'dkx0qGuAXod-XkWZerZ5',
      defaultMessage: 'I don’t want to be automatically charged',
      description: 'Cancellation reason option: does not want auto-charge',
    }),
    'Other (please specify)': intl.formatMessage({
      id: 's9fZfnhpITlOwF7irD9b',
      defaultMessage: 'Other (please specify)',
      description: 'Cancellation reason option: other with free-text input',
    }),
  }), [intl])

  const [selectedReason, setReason] = useState<string | undefined>()
  const handleReasonChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setReason(event.currentTarget.value)
  }, [])

  const fetching = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)

  const dispatch = useAppDispatch()
  const onConfirmUpdateRenewalStatus = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()

    const formObject = formToObject<CancelRenewalFormDetails>(event.currentTarget)

    dispatch(updateMemberSubscription({
      op: 'pause',
      reason: formObject.reason ? {
        category: formObject.reason.startsWith('Other') ? 'Other' : formObject.reason,
        explanation: formObject.comment,
      } : undefined,
    }))

    onRenewCancelSuccess()
  }, [dispatch, onRenewCancelSuccess])

  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const selectedOtherReason = !!selectedReason?.startsWith('Other')

  return <form onSubmit={onConfirmUpdateRenewalStatus}>
    <ModalHeader
      title={intl.formatMessage({
        id: '5WbkpEPsNFkjbtbCUlu2',
        defaultMessage: 'Cancel Renewal',
        description: 'Title for the cancel renewal modal in the cancellation or refund flow',
      })}
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <LoadingIndicator floating visible={fetching} />
        <Group direction="vertical" gap={32}>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">
              {intl.formatMessage(
                {
                  id: 'Ysua2sfeWS6JPnNR9Pjd',
                  defaultMessage: 'Thank you for being a {programName} member and we’re sorry to see you go.',
                  description: 'Heading thanking the user for their membership when cancelling renewal',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage(
                {
                  id: 'FQ6NQm5lMSXFzjhbOwkq',
                  defaultMessage: 'You’ll have access to your {programName} benefits until the end of your current membership year.',
                  description: 'Body text informing user they keep benefits until end of membership year',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </BodyText>
          </Group>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={16}>
              <BodyText variant="medium" weight="bold">{intl.formatMessage({
                id: '9TnkGyET5PVqIH-WiU0c',
                defaultMessage: 'Main reason for cancelling',
                description: 'Label for the cancellation reason selection in the cancel renewal modal',
              })}
              </BodyText>
              <Group direction="vertical" gap={8}>
                {REASONS.map((reason) => <RadioInput
                  key={reason}
                  name="reason"
                  value={reason}
                  size="medium"
                  required
                  onChange={handleReasonChange}
                >
                  {reasonLabels[reason]}
                </RadioInput>)}
              </Group>
              <Expander expanded={selectedOtherReason}>
                <TextArea
                  name="comment"
                  required={selectedOtherReason}
                  placeholder={intl.formatMessage({
                    id: '3cHjt-82CnGXW7PfgQym',
                    defaultMessage: 'Please specify',
                    description: 'Placeholder text for the free-text cancellation reason input',
                  })}
                />
              </Expander>
            </Group>
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              <LuxPlusManageAccountModalSavingsBanner />
              <LuxPlusManageAccountModalMembershipStatusBanner item={item} />
            </Group>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'zTgaREuhmqfKir1svtAK',
          defaultMessage: 'Keep my membership',
          description: 'Primary action button to keep membership and dismiss cancellation',
        }),
        onClick: dismissModal,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'ren3ZISQVRnCJt3l-gBM',
          defaultMessage: 'Cancel renewal',
          description: 'Secondary action button to confirm cancellation of renewal',
        }),
        type: 'submit',
      }}
    />
  </form>
}

export default LuxPlusManageAccountModalRenewalCancellation
